<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch, watchEffect } from 'vue'
import { BRAND } from './brand'
import { useUiStore } from './stores/ui'
import { useVaultStore } from './stores/vault'
import type { CoverId } from './stores/ui'
import TheHeader from './components/TheHeader.vue'
import WebShell from './components/web/WebShell.vue'
import MobileShell from './components/mobile/MobileShell.vue'
import { useIsMobile } from './composables/useIsMobile'
import DisguiseCover from './components/DisguiseCover.vue'
import MobileDisguiseCover from './components/mobile/MobileDisguiseCover.vue'
import Settings from './components/Settings.vue'
import Onboarding from './components/Onboarding.vue'
import Crisis from './components/Crisis.vue'
import LockScreen from './components/LockScreen.vue'
import ErrorLog from './components/ErrorLog.vue'
import QuickRecord from './components/QuickRecord.vue'
import Account from './components/account/Account.vue'
import Admin from './components/account/Admin.vue'
import { useAccountStore } from './stores/account'
import { isBrandBuild } from './brand'

const ui = useUiStore()
const vault = useVaultStore()
const account = useAccountStore()
const { isMobile } = useIsMobile()
const settingsOpen = ref(false)
const crisisOpen = ref(false)
const accountOpen = ref(false)
const adminOpen = ref(false)
const dev = import.meta.env.DEV
const brand = isBrandBuild // 계정은 브랜드 빌드에만 (익명 빌드엔 계정 없음)

// 폰 셸에서는 화려한 '앱' 표현으로(업무 모드 회색·용어 미적용). 웹의 stealth 기본값은 건드리지 않는다.
watch(isMobile, (m) => ui.setAppMode(m), { immediate: true })


/* 토스트 */
const toastMsg = ref('')
let toastT: number | undefined
function toast(msg: string) {
  toastMsg.value = msg
  clearTimeout(toastT)
  toastT = window.setTimeout(() => (toastMsg.value = ''), 2000)
}

/* body/html에 상태 반영 */
watchEffect(() => document.body.classList.toggle('stealth', ui.stealth && !isMobile.value))
watchEffect(() => document.body.classList.toggle('veil', ui.veil))
watchEffect(() => (document.documentElement.dataset.motion = ui.motionActive ? 'on' : 'off'))
watchEffect(() => ui.applyTheme()) // 테마(시스템/라이트/다크)

/* 탭 제목: 잠금 중엔 평범하게, 위장 중엔 위장 파일명, 평소엔 앱/브랜드 이름 */
const COVER_TITLE: Record<CoverId, string> = {
  sheet: '2026 4분기 예산안_v3_최종.xlsx',
  docs: 'Q4 프로젝트 회고 (초안).docx',
  mail: '받은편지함 (24)',
  kanban: '스프린트 42 · 플랫폼팀',
}
const MOBILE_COVER_TITLE: Record<string, string> = { memo: '메모', calc: '계산기', weather: '날씨', todo: '할 일' }
watchEffect(() => {
  document.title = ui.coverOn
    ? (isMobile.value ? MOBILE_COVER_TITLE[ui.opts.coverMobile] : COVER_TITLE[ui.opts.cover])
    : ui.stealth || vault.locked ? 'Workspace' : BRAND.appName
})

/* 손글씨 글꼴은 업무 모드를 끌 때만 불러온다(회사 프록시 로그에 남지 않게) */
watch(() => ui.stealth, (s) => {
  if (!s && !document.getElementById('penFont')) {
    const l = document.createElement('link')
    l.id = 'penFont'; l.rel = 'stylesheet'
    l.href = 'https://fonts.googleapis.com/css2?family=Caveat:wght@700&family=Gaegu:wght@700&display=swap'
    document.head.appendChild(l)
  }
}, { immediate: true })

/* 자리 비움(가벼운 가리개)과 금고 자동 잠금(암호화) 두 단계 */
let idleT: number | undefined
let vaultT: number | undefined
function away() {
  if (vault.locked || Date.now() < ui.awayHoldUntil) return // 의도된 blur(파일 선택 등)는 무시
  if (ui.opts.away === 'cover') ui.showCover()
  else if (ui.opts.away === 'veil' && ui.stealth) ui.setVeil(true)
}
function wake() {
  if (vault.locked) return
  ui.setVeil(false)
  clearTimeout(idleT)
  if (ui.opts.idle > 0 && !ui.coverOn) idleT = window.setTimeout(away, ui.opts.idle * 1000)
  clearTimeout(vaultT)
  const ms = (ui.opts.autoLockMin || 0) * 60000 // 설정의 자동 잠금 분(0=안 함)
  if (ms > 0) vaultT = window.setTimeout(() => { if (Date.now() < ui.awayHoldUntil) { wake(); return } vault.lock() }, ms)
}

/* 숨기기는 Esc·핫 코너, 금고 열기는 LockScreen, 위장 열기는 비밀번호(DisguiseCover) */
function onKey(e: KeyboardEvent) {
  if (vault.locked || ui.coverOn) return
  if (e.key === 'Escape') {
    e.preventDefault()
    if (settingsOpen.value) settingsOpen.value = false
    else ui.showCover()
  }
}
function onMove(e: MouseEvent) {
  if (!vault.locked && !ui.coverOn && e.clientX < 3 && e.clientY < 3) ui.showCover()
}
function onUnlock() { ui.hideCover(); wake() }

/* 금고가 잠기면 위장·설정·가리개를 걷어 다음 해제가 깨끗하게 */
watch(() => vault.locked, (locked) => {
  if (locked) {
    ui.hideCover(); ui.setVeil(false); settingsOpen.value = false
    clearTimeout(idleT); clearTimeout(vaultT)
  } else {
    wake() // 해제되면 자동 잠금 타이머 시작
  }
})

const ACT = ['mousemove', 'keydown', 'pointerdown', 'focus'] as const
onMounted(async () => {
  if (brand) account.init() // 계정 세션 복원(브랜드 빌드)
  await vault.init() // 금고 존재 여부 확인(잠긴 상태로 시작)
  ACT.forEach(t => window.addEventListener(t, wake))
  window.addEventListener('blur', away)
  window.addEventListener('keydown', onKey)
  document.addEventListener('mousemove', onMove)
})
onUnmounted(() => {
  ACT.forEach(t => window.removeEventListener(t, wake))
  window.removeEventListener('blur', away)
  window.removeEventListener('keydown', onKey)
  document.removeEventListener('mousemove', onMove)
  clearTimeout(idleT); clearTimeout(vaultT)
})
</script>

<template>
  <!-- 잠금이 최우선 게이트. init() 전에는 아무것도 안 그린다(설정/해제 화면 깜빡임 방지) -->
  <template v-if="vault.checked">
    <!-- 첫 실행 안내 -->
    <Onboarding v-if="!ui.onboarded && !vault.initialized" />
    <template v-else>
      <LockScreen v-if="vault.locked" />
      <template v-else>
        <!-- 폰: 하단 탭 앱 레이아웃 / 데스크톱·넓은 웹: 3단 레이아웃 -->
        <MobileShell
          v-if="isMobile"
          @toast="toast"
          @open-settings="settingsOpen = !settingsOpen"
          @show-cover="ui.showCover()"
          @open-account="accountOpen = true"
        />
        <template v-else>
          <TheHeader @open-settings="settingsOpen = !settingsOpen" @show-cover="ui.showCover()" @open-account="accountOpen = true" />
          <WebShell @toast="toast" />
        </template>
        <Settings v-if="settingsOpen" @changed="wake" @close="settingsOpen = false" @crisis="crisisOpen = true" />
        <Account v-if="brand && accountOpen" @close="accountOpen = false" @toast="toast" @open-admin="accountOpen = false; adminOpen = true" />
        <Admin v-if="brand && adminOpen" @close="adminOpen = false" @toast="toast" />
        <QuickRecord v-if="!ui.coverOn && !ui.overlayOpen" @toast="toast" />
        <MobileDisguiseCover v-if="ui.coverOn && isMobile" :cover="ui.opts.coverMobile" :pin="ui.opts.pin" @unlock="onUnlock" />
        <DisguiseCover v-else-if="ui.coverOn" :cover="ui.opts.cover" :pin="ui.opts.pin" @unlock="onUnlock" />
      </template>
    </template>
  </template>

  <Crisis v-if="crisisOpen" @close="crisisOpen = false" />
  <div class="toast" :class="{ on: toastMsg }" role="status" aria-live="polite">{{ toastMsg }}</div>
  <ErrorLog v-if="dev" />

</template>
